import React from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  Stack,
  TextField,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LocalOfferIcon from "@mui/icons-material/LocalOffer"; // أيقونة للعرض
import KitchenIcon from "@mui/icons-material/Kitchen"; // أيقونة المطبخ
import { removeOfferFromOrder } from "../../../../../../../../services/pos/reservation/reservationApi"; // إضافة استيراد دالة حذف العرض
import { usePermissions } from "../../../../../../../../context/PermissionsAndFeatures/UserPermissionsContext";

const OrderItemsList = ({
  items,
  scrollThreshold = 4,
  onDeleteItem,
  onEditItem,
  onMoveItem,
  // props الجديدة
  preparationCasherEnabled = false,
  onPrepareItem = () => {},
  // NEW: دالة لتغيير حالة الصنف عبر معرفه
  changeOrderItemStatus = null,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuItemId, setMenuItemId] = React.useState(null);

  // حالة عرض تفاصيل الصنف
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsItem, setDetailsItem] = React.useState(null);
  const { hasPermission } = usePermissions();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleMenuOpen = (event, itemId) => {
    setMenuAnchor(event.currentTarget);
    setMenuItemId(itemId);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItemId(null);
  };

  const handleShowDetails = (item) => {
    setDetailsItem(item);
    setDetailsOpen(true);
    handleMenuClose();
  };

  // معالج حذف العرض مباشرة عبر API الخاص بـ reservation
  const handleRemoveOffer = async (item) => {
    const orderId = item.order_id || item.orderId;
    if (!orderId) return alert("❌ لا يمكن حذف العرض: معرف الطلب مفقود.");
    if (!window.confirm("❗ هل تريد حذف هذا العرض من الطلب؟")) return;

    try {
      await removeOfferFromOrder(orderId, item.id);
      alert("✅ تم حذف العرض من الطلب بنجاح");

      // إشعار لباقي الأجزاء (مثل TablesSection / OrderDetailsFetcher) لإعادة التحميل
      window.dispatchEvent(
        new CustomEvent("orderUpdated", {
          detail: { orderId, tableId: item.table_id || null },
        })
      );
    } catch (err) {
      alert(err.message || "❌ فشل في حذف العرض");
    }
  };

  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        لا توجد عناصر في هذا الطلب.
      </Typography>
    );
  }

  const scrollHeight = items.length > scrollThreshold ? 400 : "auto";

  const subdomain = window.location.hostname.split(".")[0];
  const IMAGE_BASE_URL = `https://${subdomain}.restocash.app/`;
  const placeholderImage = "https://via.placeholder.com/300x140?text=No+Image";

  return (
    <Box mt={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 1.5,
          maxHeight: { xs: 300, sm: 380, md: 500 },
          overflowY: items.length > scrollThreshold ? "auto" : "visible",
          px: { xs: 1, sm: 0 },
        }}
      >
        {items.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              bgcolor: "#232334",
              borderRadius: 3,
              p: { xs: 1, sm: 1.5 },
              boxShadow: 2,
              gap: { xs: 1, sm: 0 },
              position: "relative",
            }}
          >
            {/* صورة المنتج + حالة الطلب */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                ml: { xs: 0, sm: 2 },
              }}
            >
              <Avatar
                src={
                  item.product_image
                    ? `${IMAGE_BASE_URL}${item.product_image.replace(
                        / /g,
                        "%20"
                      )}`
                    : placeholderImage
                }
                alt={item.product_name}
                sx={{
                  width: { xs: 44, sm: 54, md: 64 },
                  height: { xs: 44, sm: 54, md: 64 },
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              />
              {/* أيقونة توضح أن هذا العنصر هو عرض؛ النقر يفتح تفاصيل الأصناف الداخلة بالعرض */}
              {item.is_offer === 1 && (
                <Tooltip title="عرض محتوى العرض">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDetailsItem(item);
                      setDetailsOpen(true);
                    }}
                    sx={{ mt: 0.5, color: "#fff" }}
                  >
                    <LocalOfferIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Typography
                variant="caption"
                color={
                  item.status === "finished"
                    ? "#4caf50"
                    : item.status === "in_prepration"
                    ? "#ff9800"
                    : item.status === "preparing"
                    ? "#1976d2"
                    : "#ffeb3b"
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 0.5,
                  fontSize: { xs: 11, sm: 12 },
                }}
              >
                <span
                  role="img"
                  aria-label="timer"
                  style={{ fontSize: 14, marginLeft: 4 }}
                >
                  ⏱️
                </span>
                {item.status === "finished"
                  ? "تم الانتهاء"
                  : item.status === "in_prepration"
                  ? "تم الإرسال للتجهيز"
                  : item.status === "preparing"
                  ? "قيد التجهيز"
                  : item.status === "under_review"
                  ? "طلب خارجي قيد المراجعة"
                  : "قيد التجهيز"}
              </Typography>
            </Box>

            {/* بيانات الصنف */}
            <Box
              sx={{ flex: 1, minWidth: 0, mr: { xs: 0, sm: 2 }, width: "100%" }}
            >
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  fontWeight: 600,
                  color: "#fff",
                  fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  mb: 0.5,
                  maxWidth: { xs: "100%", sm: "150px" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.product_name}
              </Typography>
              <Typography
                variant="caption"
                color="#aaa"
                sx={{ display: "block", mb: 0.5 }}
              >
                {item.note || "ملاحظة الصنف."}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" color="#aaa">
                  ${item.price}
                </Typography>
              </Stack>
            </Box>

            {/* الكمية */}
            <Box sx={{ mx: 1 }}>
              <TextField
                size="small"
                value={item.quantity}
                InputProps={{
                  readOnly: true,
                  sx: {
                    width: { xs: 34, sm: 38 },
                    textAlign: "center",
                    color: "white",
                    input: { textAlign: "center", p: 0.5 },
                  },
                }}
                variant="outlined"
              />
            </Box>

            {/* السعر فوق الإجراءات */}
            <Box
              sx={{
                minWidth: { xs: 50, sm: 60 },
                textAlign: "center",
                mx: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: "#fff", mb: 0.5, fontSize: { xs: 13, sm: 14 } }}
              >
                ${item.total}
              </Typography>
              {/* زر الإجراءات الموحد */}
              <Tooltip title="إجراءات">
                <IconButton
                  color="primary"
                  sx={{
                    border: "1px solid #888",
                    bgcolor: "#232334",
                    width: { xs: 30, sm: 32 },
                    height: { xs: 30, sm: 32 },
                  }}
                  onClick={(e) => handleMenuOpen(e, item.id)}
                >
                  <MoreVertIcon
                    sx={{ color: "white", fontSize: { xs: 14, sm: 18 } }}
                  />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={menuItemId === item.id}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                sx={{
                  "& .MuiMenu-paper": {
                    borderRadius: 2,
                    minWidth: 140,
                    boxShadow: 3,
                  },
                }}
              >
                <MenuItem onClick={() => handleShowDetails(item)}>
                  تفاصيل الصنف
                </MenuItem>
                {hasPermission("orders_tables_print_table") && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      onEditItem?.(item);
                    }}
                  >
                    <EditIcon
                      sx={{ color: "#1976d2", mr: 1 }}
                      fontSize="small"
                    />
                    تعديل
                  </MenuItem>
                )}
                {hasPermission("orders_other_delete_items") && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      // لو العنصر عرض: استعمل removeOfferFromOrder مباشرة
                      if (item.is_offer === 1) {
                        handleRemoveOffer(item);
                      } else {
                        onDeleteItem?.(item.id);
                      }
                    }}
                    sx={{ color: "#ff4d4f" }}
                  >
                    <DeleteIcon
                      sx={{ color: "#ff4d4f", mr: 1 }}
                      fontSize="small"
                    />
                    حذف
                  </MenuItem>
                )}
                {hasPermission("orders_other_edite_price") && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      onMoveItem?.(item);
                    }}
                  >
                    <SwapHorizIcon
                      sx={{ color: "#1976d2", mr: 1 }}
                      fontSize="small"
                    />
                    نقل
                  </MenuItem>
                )}
              </Menu>
            </Box>

            {/* زر التجهيز */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {preparationCasherEnabled && item.status === "pending" && (
                <Tooltip title="وضع الصنف قيد التجهيز" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // استعمل changeOrderItemStatus إذا كانت معطاة (ترسل id) وإلا استعمل onPrepareItem كنسخة احتياطية
                      if (typeof changeOrderItemStatus === "function") {
                        changeOrderItemStatus(item.id);
                      } else {
                        onPrepareItem(item);
                      }
                    }}
                    sx={{
                      bgcolor: "#fff0f6",
                      color: "#f48fb1",
                      "&:hover": { bgcolor: "#ffe6f0" },
                      width: { xs: 30, sm: 34 },
                      height: { xs: 30, sm: 34 },
                    }}
                  >
                    <KitchenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* مؤشر يوضح أن العنصر قيد التجهيز بالفعل */}
              {item.status === "preparing" && (
                <Tooltip title="قيد التجهيز" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "#e8f5e9",
                      color: "#388e3c",
                      width: { xs: 30, sm: 34 },
                      height: { xs: 30, sm: 34 },
                    }}
                    disabled
                  >
                    <KitchenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* NEW: مؤشر حالة "تم الإرسال للتجهيز" */}
              {item.status === "in_prepration" && (
                <Tooltip title="تم الإرسال للتجهيز" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "#fff3e0",
                      color: "#fb8c00",
                      width: { xs: 30, sm: 34 },
                      height: { xs: 30, sm: 34 },
                    }}
                    disabled
                  >
                    <KitchenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Dialog لعرض تفاصيل الصنف */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>تفاصيل الصنف</DialogTitle>
        <DialogContent>
          {detailsItem && (
            <>
              {/* إذا كان العنصر عرض - نعرض قائمة الأصناف الداخلة */}
              {detailsItem.is_offer === 1 &&
              Array.isArray(detailsItem.items) ? (
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    {detailsItem.product_name} (عرض)
                  </Typography>
                  {detailsItem.items.map((it) => (
                    <Box
                      key={it.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Avatar
                        src={
                          it.product_image
                            ? `${IMAGE_BASE_URL}${it.product_image.replace(
                                / /g,
                                "%20"
                              )}`
                            : placeholderImage
                        }
                        alt={it.product_name}
                        sx={{ width: 56, height: 56 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {it.product_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          الكمية: {it.quantity} — السعر: ${it.price} — الإجمالي:
                          ${it.total}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                /* عرض تفاصيل صنف واحد عادي */
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    {detailsItem.product_name}
                  </Typography>
                  <Avatar
                    src={
                      detailsItem.product_image
                        ? `${IMAGE_BASE_URL}${detailsItem.product_image.replace(
                            / /g,
                            "%20"
                          )}`
                        : placeholderImage
                    }
                    alt={detailsItem.product_name}
                    sx={{ width: 80, height: 80, mb: 2, mx: "auto" }}
                  />
                  <Typography variant="body2" mb={1}>
                    <strong>الكمية:</strong> {detailsItem.quantity}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>السعر:</strong> ${detailsItem.price}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>الإجمالي:</strong> ${detailsItem.total}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>ملاحظة:</strong> {detailsItem.note || "لا يوجد"}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>الحالة:</strong>{" "}
                    {detailsItem.status === "finished"
                      ? "تم الانتهاء"
                      : detailsItem.status === "in_prepration"
                      ? "تم الإرسال للتجهيز"
                      : detailsItem.status === "preparing"
                      ? "قيد التجهيز"
                      : "قيد التجهيز"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} color="primary">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderItemsList;
